import Loader from 'Components/Common/Loader';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { createCampaignFromIdea, fetchCampaignIdeas, fetchCampaignStrategies, generateCampaignIdeas } from 'services/api/campaign';
import { AppDispatch, RootState } from 'store';
import { CampaignIdea, CampaignStrategy } from 'types/campaign';
import { getErrorMessage } from 'utils/helpers';
import '../../../pages/Campaigns/style.scss';
import { Link, useNavigate } from 'react-router-dom';
import { UserBrand } from 'types/users';
import { fetchUserBrands as fetchUserBrandsThunk } from 'slices/users/brands/thunk';
import AICampaignCreationLayout from 'Layouts/AICampaignCreationLayout';
import AICampaignIdeas from './AICampaignIdeas';

const POLL_INTERVAL = 10000; //10 seconds

const CampaignCreationAIStrategies: React.FC<{ brandSocialAccountId: string | number, onBrandChange?: (brand: UserBrand | null) => void }> = ({ brandSocialAccountId, onBrandChange }) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const [campaignStrategies, setCampaignStrategies] = useState<CampaignStrategy[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedStrategyUuid, setSelectedStrategyUuid] = useState<string>('');
    const userBrands = useSelector((state: RootState) => state.userBrands.items);
    const [brandMenuOpen, setBrandMenuOpen] = useState(false);
    const brandMenuRef = React.useRef<HTMLDivElement | null>(null);
    const [generatingIdeas, setGeneratingIdeas] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [ideasGeneratedAt, setIdeasGeneratedAt] = useState<string | null>(null);
    const [newIdeasAvailable, setNewIdeasAvailable] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState<CampaignIdea | null>(null);
    const [fetchingIdeas, setFetchingIdeas] = useState(false);

    const [campaignIdeas, setCampaignIdeas] = useState<CampaignIdea[]>([]);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchUserBrandsThunk(false));
    }, [dispatch]);

    useEffect(() => {
        if (!brandMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const el = brandMenuRef.current;
            if (!el) return;
            if (e.target instanceof Node && el.contains(e.target)) return;
            setBrandMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [brandMenuOpen]);


    const createCampaign = async () => {
        if (!selectedIdea || !selectedAgencyId) return;
        setCreatingCampaign(true);
        try {
            const response = await createCampaignFromIdea(selectedAgencyId, selectedIdea.uuid);
            if (response.campaign?.uuid) {
                navigate(`/campaign/${response.campaign.uuid}/edit`);
            }

        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create campaign'));
        } finally {
            setCreatingCampaign(false);
        }

    }

    const fetchIdeas = async (strategyUuid: string = selectedStrategyUuid) => {
        if (!selectedAgencyId || !brandSocialAccountId || !strategyUuid) return;
        setFetchingIdeas(true);
        try {
            const response = await fetchCampaignIdeas(selectedAgencyId, brandSocialAccountId, strategyUuid);
            setCampaignIdeas(response?.items ?? []);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign ideas'));
        } finally {
            setFetchingIdeas(false);
        }
    }




    const fetchStrategies = async () => {
        if (!selectedAgencyId || !brandSocialAccountId) return;
        try {
            setLoading(true);
            const response = await fetchCampaignStrategies(selectedAgencyId, brandSocialAccountId);
            const items = (response as any)?.items ?? (response as any)?.data ?? response;
            if (Array.isArray(items)) {
                setCampaignStrategies(items);
                if (!selectedStrategyUuid && items[0]?.uuid) setSelectedStrategyUuid(items[0].uuid);

                const strategyId = selectedStrategyUuid ?? (items[0]?.uuid ?? null);
                // console.log('selectedStrategyUUId', strategyId);
                if (strategyId) {
                    const strategy = items.find((s: CampaignStrategy) => s.uuid === strategyId);
                    // console.log('selected strategy update', strategy);
                    // console.log('selected strategy old', selectedStrategy);
                    if (strategy && selectedStrategy && strategy.uuid === selectedStrategy.uuid) {
                        // console.log('selected strategy same');
                        if (generatingIdeas) {
                            // console.log('generating ideas');
                            // console.log('strategy new ideas generated at', strategy.ideas_generated_at);
                            // console.log('selected strategy old ideas generated at', selectedStrategy.ideas_generated_at);
                            // console.log('new Date(strategy.ideas_generated_at).getTime()', new Date(strategy.ideas_generated_at ?? '').getTime());
                            // console.log('new Date(selectedStrategy.ideas_generated_at).getTime()', new Date(selectedStrategy.ideas_generated_at ?? '').getTime());
                            // console.log('new Date(strategy.ideas_generated_at).getTime() > new Date(selectedStrategy.ideas_generated_at).getTime()', new Date(strategy.ideas_generated_at ?? '').getTime() > new Date(selectedStrategy.ideas_generated_at ?? '').getTime());
                            if ((new Date(strategy.ideas_generated_at ?? 0).getTime()) > new Date(selectedStrategy.ideas_generated_at ?? 0).getTime()) {
                                setNewIdeasAvailable(true);
                                fetchIdeas();
                            } else {
                                // console.log('no new ideas available');
                                setTimeout(() => { fetchStrategies() }, POLL_INTERVAL);
                            }
                        } else {
                            // console.log('no generating ideas');
                        }
                    } else {
                        // console.log('selected strategy different');
                    }
                } else {
                    // console.log('No strategy Selected or strategies fetched first time.')
                    // setNewIdeasAvailable(true); // For Testing purpose
                    // console.log(items[0]?.uuid);
                    // fetchIdeas(items[0]?.uuid);
                }

            } else {
                setCampaignStrategies([]);
                setIdeasGeneratedAt(null);
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch campaign strategies'));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!selectedAgencyId || !brandSocialAccountId) return;
        fetchStrategies();
    }, [brandSocialAccountId, selectedAgencyId]);


    useEffect(() => {
        if (generatingIdeas && brandSocialAccountId && selectedAgencyId) {
            setTimeout(() => { fetchStrategies() }, POLL_INTERVAL);
        }
    }, [generatingIdeas, brandSocialAccountId, selectedAgencyId])


    const selectedStrategy = useMemo(() => {
        return campaignStrategies.find((s: CampaignStrategy) => s.uuid === selectedStrategyUuid) ?? campaignStrategies[0] ?? null;
    }, [campaignStrategies, selectedStrategyUuid]);


    const generateIdeas = async () => {
        if (!selectedStrategy || !selectedAgencyId || !brandSocialAccountId) return;
        setGenerating(true);
        try {
            await generateCampaignIdeas(selectedAgencyId, brandSocialAccountId, selectedStrategy.uuid);
            setGeneratingIdeas(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to generate campaign ideas'));
            setGeneratingIdeas(false);
        } finally {
            setGenerating(false);
        }
    }

    if (generatingIdeas && !newIdeasAvailable) {
        return (
            <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
                <div className="loader mb-2"></div>
                <div className="mt-2 text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>
                    Generating Ideas...
                </div>
            </div>
        );
    }

    if (loading) return <Loader />

    return (
        <AICampaignCreationLayout
            steps={[
                {
                    label: 'Choose Strategy',
                    title: 'Choose a Strategy',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: !newIdeasAvailable,
                    completed: newIdeasAvailable,
                },
                {
                    label: 'Choose Campaign',
                    title: 'Choose a Campaign',
                    subtitle: 'Each campaign is a ready-to-deploy content brief aligned with this strategy.',
                    active: newIdeasAvailable,
                    completed: false,
                },
                {
                    label: 'Review',
                    title: 'Review',
                    subtitle: 'Review the campaign and make any final adjustments.',
                    active: false,
                    completed: false,
                }
            ]}
            brandSocialAccountId={brandSocialAccountId}
            onBrandChange={onBrandChange}
            bottomBarContent={
                newIdeasAvailable ? (
                    <button
                        type="button"
                        className="btn btn-gradient"
                        disabled={!selectedIdea || creatingCampaign}
                        onClick={createCampaign}
                    >
                        {creatingCampaign ? 'CREATING CAMPAIGN...' : 'USE THIS CAMPAIGN SETUP'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-gradient"
                        disabled={!selectedStrategy || generating}
                        onClick={generateIdeas}
                    >
                        {generating ? 'PLEASE WAIT...' : 'USE THIS STRATEGY'}
                    </button>
                )
            }
        >
            {
                newIdeasAvailable ? (<AICampaignIdeas loading={fetchingIdeas} campaignIdeas={campaignIdeas} onIdeaSelect={setSelectedIdea} />) :
                    (
                        campaignStrategies.length > 0 ? (
                            <div className="campaign-ai-creation__grid">
                                <div className="campaign-ai-creation__left" role="listbox" aria-label="Strategy list">
                                    {campaignStrategies.map((s) => {
                                        const selected = s.uuid === selectedStrategyUuid;
                                        return (
                                            <button
                                                key={s.uuid}
                                                type="button"
                                                className={`campaign-ai-creation-item${selected ? ' campaign-ai-creation-item--selected' : ''}`}
                                                onClick={() => setSelectedStrategyUuid(s.uuid)}
                                                role="option"
                                                aria-selected={selected}
                                            >
                                                <div className="campaign-ai-creation-item__name">{s.name}</div>
                                                <div className="campaign-ai-creation-item__subline">{s.strategy.content_style}</div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="campaign-ai-creation__right" aria-label="Strategy details">
                                    {selectedStrategy && (
                                        <div className="campaign-ai-creation-detail">
                                            <div className="campaign-ai-creation-detail__title">{selectedStrategy.name}</div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">THE PITCH</div>
                                                <div className="campaign-ai-field">{selectedStrategy.pitch}</div>
                                            </div>

                                            <div className="campaign-ai-miniGrid" aria-label="Creator tier and hooks">
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="campaign-ai-miniGrid-label">CREATOR TIER</div>
                                                    <div className="campaign-ai-miniGrid-value">{selectedStrategy.strategy?.creator_criteria?.tier_focus ?? '—'}</div>
                                                </div>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="campaign-ai-miniGrid-label">HOOKS</div>
                                                    <div className="campaign-ai-miniGrid-value">{selectedStrategy.strategy.hooks.join(' • ')}</div>
                                                </div>
                                                <div></div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">TARGET AUDIENCE</div>
                                                <div className="campaign-ai-card">
                                                    <div className="campaign-ai-card__row">
                                                        <div className="campaign-ai-subcard">
                                                            <div className="campaign-ai-subcard__k">PRIMARY SEGMENT</div>
                                                            <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.primary_segment ?? '—'}</div>
                                                        </div>
                                                        <div className="campaign-ai-subcard">
                                                            <div className="campaign-ai-subcard__k">SECONDARY SEGMENT</div>
                                                            <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.secondary_segment ?? '—'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="campaign-ai-subcard campaign-ai-subcard--full">
                                                        <div className="campaign-ai-subcard__k">PSYCHOGRAPHIC PROFILE</div>
                                                        <div className="campaign-ai-subcard__v">{selectedStrategy.strategy?.target_demography?.psychographic_profile ?? '—'}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">CREATOR PROFILE</div>
                                                <div className="campaign-ai-field">{selectedStrategy.strategy?.creator_criteria?.qualitative_traits ?? '—'}</div>
                                            </div>

                                            <div className="campaign-ai-section">
                                                <div className="campaign-ai-section__label">30-60 DAYS EXECUTION PLAN</div>
                                                <div className="campaign-ai-field">{selectedStrategy.strategy?.social_media_strategy_30_60_days ?? '—'}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
                                <div className="text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>No strategies found</div>
                            </div>
                        )
                    )
            }

        </AICampaignCreationLayout>
    )

}

export default CampaignCreationAIStrategies